/**
 * usePushNotifications
 *
 * Handles:
 *  1. Prompting for browser Notification permission after onboarding completion
 *  2. Subscribing to Web Push (VAPID) and posting the subscription to the backend
 *  3. Listening for Socket.IO `push_notification` events to show Sonner toasts
 */

import { useEffect } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

declare const __VAPID_PUBLIC_KEY__: string;

export const PUSH_SUBSCRIBED_KEY = 'push_subscribed';
export const PUSH_OPTED_OUT_KEY = 'push_opted_out';

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const arr = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    arr[i] = rawData.charCodeAt(i);
  }
  return arr;
}

async function fetchVapidKey(): Promise<string | null> {
  try {
    const res = await api.get<{ success: boolean; data: { vapidPublicKey: string } }>('/notifications/vapid-key');
    return res.data.vapidPublicKey || null;
  } catch {
    return null;
  }
}

export async function subscribeToPush(): Promise<void> {
  // Prefer server-provided key (eliminates env var sync issues),
  // fall back to build-time / env var values.
  const vapidKey =
    (await fetchVapidKey()) ||
    __VAPID_PUBLIC_KEY__ ||
    import.meta.env.VITE_VAPID_PUBLIC_KEY;

  if (!vapidKey) {
    console.warn('[usePushNotifications] VAPID public key not configured');
    return;
  }

  const registration = await navigator.serviceWorker.ready;

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey),
  });

  const subscriptionJson = subscription.toJSON();
  const p256dhKey = subscriptionJson.keys?.p256dh;
  const authKey = subscriptionJson.keys?.auth;

  if (!subscriptionJson.endpoint || !p256dhKey || !authKey) {
    throw new Error('Incomplete push subscription keys');
  }

  await api.post('/notifications/subscribe', {
    endpoint: subscriptionJson.endpoint,
    p256dhKey,
    authKey,
    platform: navigator.userAgent.includes('Mobi') ? 'mobile' : 'desktop',
  });

  localStorage.setItem(PUSH_SUBSCRIBED_KEY, '1');
}

export async function unsubscribeFromPush(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    await api.delete('/notifications/unsubscribe', { data: { endpoint: subscription.endpoint } });
    await subscription.unsubscribe();
  }

  localStorage.removeItem(PUSH_SUBSCRIBED_KEY);
}

export function usePushNotifications() {
  const { user } = useAuth();

  // ── Auto-subscribe on login (default ON) ────────────────────────────────
  // No app-level prompt — browser's native permission dialog fires if needed.
  // User can disable in Privacy Settings, which sets PUSH_OPTED_OUT_KEY.
  useEffect(() => {
    if (!user?.onboardingCompleted) return;
    if (localStorage.getItem(PUSH_SUBSCRIBED_KEY)) return;
    if (localStorage.getItem(PUSH_OPTED_OUT_KEY) === '1') return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
    if (Notification.permission === 'denied') return;

    subscribeToPush().catch((err) => {
      console.warn('[usePushNotifications] Auto push subscription failed:', err);
    });
  }, [user?.onboardingCompleted]);

  // Real-time push notifications are handled by NotificationCenterModal
  // which auto-opens the panel when a push_notification socket event arrives.
}
